import { memo, Fragment } from 'react';
import { Box, useTheme } from '@mui/material';
import { useLocalStorage } from '@mantine/hooks';
import { kpiBarExpandedMinHeight } from 'utility/constants';
import { KpiBarStorageState } from 'components/Dashboard/widgets/KPIs/KpiBar';
import { useSearch } from './SearchContextProvider';
import { searchResultsWrapperSx } from './styles';

/**
 * SearchView components layout
 * kpibar: KPI bar
 * savedSearchModal: Saved Search Modal
 * -- requires ctx SavedSearchContextProvider
 *
 * batchEditModal: Batch Edit Modal
 * -- requires ctx SearchItemsProvider then BatchEditContextProvider
 *
 * footerBar: bar for batch edit
 * -- requires ctx wrappers as batchEditModal
 *
 * searchInputs: Search inputs (new +, search bar, etc)
 * searchResults: Search results (list, summary, etc)
 *
 */
interface SearchViewProps {
  kpiBar?: React.ReactNode;
  savedSearchModal?: React.ReactNode; // save this search modal
  batchEditModal?: React.ReactNode; // just inner modal for batch edit
  footerBar?: React.ReactNode; // footer bar for batch edit
  searchInputs?: React.ReactNode; // search top bar area (view, inputs, etc)
  searchResults?: React.ReactNode; // search results
  children?: React.ReactNode;
}

/**
 * SearchView components layout
 */
export const SearchView = memo(
  ({
    kpiBar,
    searchInputs,
    searchResults,
    footerBar,
    savedSearchModal,
    batchEditModal,
    children,
  }: SearchViewProps) => {
    const theme = useTheme();
    const { view, kpibarStateStorageKey, accountType } = useSearch();

    const [kpiBarState] = useLocalStorage<KpiBarStorageState>({
      key: kpibarStateStorageKey || 'kpibar',
      defaultValue: { open: null },
    });

    const isKpiBarExpanded = kpiBarState?.open && accountType !== 'admin';

    return (
      <Fragment>
        {savedSearchModal}
        {batchEditModal}
        {children}
        <Box
          id="searchView"
          sx={{
            container: 'searchView / inline-size',
            display: 'grid',
            gridTemplateAreas: `
                    'searchKpis'
                    'searchInputs'
                    'searchResults'
                    'searchFooter'
                `,
            gridTemplateRows: 'auto auto 1fr auto',
            gridTemplateColumns: '1fr',
            overflow: 'hidden',
            height: '100%',
            width: '100%',
            minHeight: '100%',
            minWidth: '100%',
          }}
        >
          <Box
            id="searchKpis"
            sx={{
              gridArea: 'searchKpis',
              container: 'searchKpis / inline-size',
              overflow: 'auto',
              minHeight: isKpiBarExpanded ? kpiBarExpandedMinHeight : 'auto', // Prevent reflow
            }}
          >
            {kpiBar}
          </Box>
          <Box
            id="searchInputs"
            sx={{
              gridArea: 'searchInputs',
              container: 'searchInputs / inline-size',
              overflow: 'auto',
            }}
          >
            {searchInputs}
          </Box>
          <Box
            id="searchResults"
            sx={searchResultsWrapperSx(theme, view)}
          >
            {searchResults}
          </Box>
          <Box
            id="searchFooter"
            sx={{
              gridArea: 'searchFooter',
              container: 'searchFooter / inline-size',
              overflow: 'auto',
            }}
          >
            {footerBar}
          </Box>
        </Box>
      </Fragment>
    );
  },
);
SearchView.displayName = 'SearchViewWithContext';
